import { useMemo } from 'react';
import { useCommandPaletteIndex } from './useCommandPaletteSearch.js';

// Curated highlights for the palette's default view, in order. Each names a page by where it lives, so the entry
// itself (label, icon, path) comes from the same permission-filtered index the search uses: a page the user cannot
// open is simply not in it, and so never suggested. Inside a game, its everyday pages lead; the hub pages fill in
// (and are all there is outside a game).
const GAME_SUGGESTIONS = [
    { segment: 'dashboard', subtitle: 'Overview of key game metrics' },
    { segment: 'live-events', subtitle: 'Plan and run live events' },
    { segment: 'segments', subtitle: 'Player segmentation' },
    { segment: 'configs', subtitle: 'Game configuration' },
    { segment: 'settings/details', subtitle: 'Game settings' },
];
const HUB_SUGGESTIONS = [
    { path: '/game', subtitle: 'Games you can open' },
    { path: '/portfolio', subtitle: 'Your portfolios' },
    { path: '/profile', subtitle: 'Your account' },
];
const MAX_SUGGESTIONS = 5;

/** Picks the suggestions out of a palette index (pure; exported for tests). */
export function pickSuggestions(index) {
    const byPath = new Map(index.map(e => [e.path, e]));
    const gameBase = index.map(e => e.path.match(/^(\/game\/[^/]+)\/.+/)?.[1]).find(Boolean) || null;
    const picks = [
        ...(gameBase ? GAME_SUGGESTIONS.map(s => ({ ...s, path: `${gameBase}/${s.segment}` })) : []),
        ...HUB_SUGGESTIONS,
    ];
    const out = [];
    for (const { path, subtitle } of picks) {
        const entry = byPath.get(path);
        if (entry) out.push({ ...entry, id: `suggest-${path}`, subtitle: entry.subtitle ? `${entry.subtitle} · ${subtitle}` : subtitle });
        if (out.length === MAX_SUGGESTIONS) break;
    }
    return out;
}

/** "Suggested for you": a few everyday pages the user can open in the current scope. */
export function useCommandPaletteSuggestions() {
    const index = useCommandPaletteIndex();
    return useMemo(() => pickSuggestions(index), [index]);
}
